import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AxiosInstance, AxiosError } from 'axios';
import axios from 'axios';
import Bugsnag from '@bugsnag/js';
import { message } from 'antd';

// Import all API SDK endpoints.
import {
  AlertsApi,
  AnalyticsApi,
  CompaniesApi,
  ComplianceApi,
  DnbApi,
  FeedbackApi,
  IntegrationsApi,
  NewsApi,
  PortfoliosApi,
  ProfilesApi,
  TokenApi,
  BindersApi,
  BillingApi,
  ActivitiesApi,
  AssistantApi,
  Configuration,
} from 'src/api-sdk';

import i18n, { normalizeLanguage } from './i18n';
import { queryClient } from './query-client';
import { QUERIES } from './globals';
import { SUPPORT_REQUEST_PATH } from './use-billing-gating';

/** Shape of the 403 body the backend returns when a credit budget is exhausted. */
interface CreditsExhaustedError {
  detail?: string;
  code?: string;
  reason?: 'hard_cap' | 'expired_no_renewal' | 'no_billing_period';
  kind?: 'company' | 'ubo_call' | 'compliance_check';
}

/**
 * Central handler for `credits_exhausted` 403 responses. Surfaces the backend's
 * `detail` message plus a support link (never a blank error) and refetches the
 * billing usage so pre-gating catches up. Does not retry or swallow the error —
 * local `.catch` handlers still run.
 */
function handleCreditsExhausted(err: AxiosError): void {
  if (err.response?.status !== 403) return;
  const body = err.response.data as CreditsExhaustedError | undefined;
  if (body?.code !== 'credits_exhausted') return;

  const detail =
    body.detail || i18n.t('You have reached a usage limit.', { ns: 'billing' });
  message.error({
    key: 'credits_exhausted',
    content: (
      <>
        {detail}{' '}
        <a href={SUPPORT_REQUEST_PATH}>
          {i18n.t('Raise a support request', { ns: 'billing' })}
        </a>
      </>
    ),
    duration: 8,
  });

  void queryClient.invalidateQueries({
    queryKey: [QUERIES.BILLING_STATISTICS],
  });
}

/**
 * Decides whether an Axios error from the API should be reported to Bugsnag.
 * Cancelled requests and handled 4xx responses (auth refresh flow, validation
 * errors, credits_exhausted, etc.) are expected and would otherwise flood
 * Bugsnag. 5xx and no-response (network/unexpected) errors still get reported.
 */
export function shouldReportApiError(err: AxiosError): boolean {
  if (axios.isCancel(err) || err.code === 'ERR_CANCELED') return false;
  // Browser-aborted requests (navigation/unload) reuse ECONNABORTED but keep
  // axios's fixed "Request aborted" message; timeouts use the same code with
  // a distinct "timeout of Xms exceeded" message and must still be reported.
  if (
    err.code === axios.AxiosError.ECONNABORTED &&
    err.message === 'Request aborted'
  )
    return false;
  const status = err.response?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return true;
}

export type APIType = {
  analyticsAPI: AnalyticsApi;
  alertsAPI: AlertsApi;
  portfoliosAPI: PortfoliosApi;
  newsAPI: NewsApi;
  feedbackAPI: FeedbackApi;
  companiesAPI: CompaniesApi;
  dnbAPI: DnbApi;
  integrationsApi: IntegrationsApi;
  profilesAPI: ProfilesApi;
  complianceAPI: ComplianceApi;
  tokenAPI: TokenApi;
  bindersAPI: BindersApi;
  billingAPI: BillingApi;
  activitiesAPI: ActivitiesApi;
  assistantAPI: AssistantApi;
};

interface APIContextInterface {
  api: AxiosInstance;
  baseApiUrl: string;
  authHeaderSet: boolean;
  setAuthorization: (token: string | null, scheme?: string) => void;
  configuration: Configuration;
}

// Create a context for the API instance.
const APIContext = createContext<APIContextInterface | null>(null);

/* ============================================================
   Provider: Creates and provides a configured API instance.
   ============================================================ */

export const ProvideAPI = ({ children }: { children: ReactNode }) => {
  const value = useProvideAPI();
  return <APIContext.Provider value={value}>{children}</APIContext.Provider>;
};

function useProvideAPI(): APIContextInterface {
  const baseApiUrl = process.env.NEXT_PUBLIC_API_URL as string;
  const [authHeaderSet, setAuthHeaderSet] = useState(false);
  const configuration = React.useMemo(() => {
    // console.log('Base path for API', baseApiUrl);
    return new Configuration({
      basePath: baseApiUrl,
    });
  }, [baseApiUrl]);

  // Create the Axios instance only once using useMemo.
  const api: AxiosInstance = useMemo(() => {
    const instance = axios.create({
      baseURL: baseApiUrl,
      headers: { 'Content-Type': 'application/json' },
    });
    // Example interceptor for requests.
    instance.interceptors.request.use(
      (config) => config,
      (error) => Promise.reject(error)
    );
    // Response interceptor to enrich Bugsnag error reports with HTTP metadata.
    instance.interceptors.response.use(
      (res) => res,
      (err: AxiosError) => {
        // Backstop for credit-exhausted 403s that slip past pre-gating (e.g. a
        // concurrent request spending the last credit). Show a clear toast with
        // a support link and refresh the usage widgets. Do NOT retry.
        handleCreditsExhausted(err);
        // Only notify Bugsnag if it's configured and initialized
        if (
          process.env.NEXT_PUBLIC_BUGSNAG_API_KEY &&
          shouldReportApiError(err)
        ) {
          try {
            if (Bugsnag.isStarted()) {
              const { config, response, code, message } = err;
              Bugsnag.notify(err, (event) => {
                event.addMetadata('http', {
                  method: config?.method,
                  url: config?.url,
                  baseURL: config?.baseURL,
                  status: response?.status,
                  statusText: response?.statusText,
                  code,
                  message,
                  hasResponse: !!response,
                });
              });
            }
          } catch (bugsnagError) {
            // Silently fail if Bugsnag is not available
            console.warn('Failed to notify Bugsnag:', bugsnagError);
          }
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [baseApiUrl]);

  // Keep Accept-Language in sync with the active i18n language.
  useEffect(() => {
    const applyLanguage = (lang?: string) => {
      api.defaults.headers.common['Accept-Language'] = normalizeLanguage(lang);
    };

    applyLanguage(i18n.language);

    const handleLanguageChange = (lang: string) => applyLanguage(lang);
    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [api]);

  /**
   * Set (or clear) the Authorization header.
   * @param token  The auth token (e.g. JWT string). If null, the header is removed.
   * @param scheme The auth scheme (default is "Bearer").
   */
  const setAuthorization = (token: string | null, scheme = 'Bearer') => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `${scheme} ${token}`;
      setAuthHeaderSet(true);
    } else {
      delete api.defaults.headers.common['Authorization'];
      setAuthHeaderSet(false);
    }
  };

  return {
    api,
    baseApiUrl,
    configuration,
    authHeaderSet,
    setAuthorization,
  };
}

/* ============================================================
   Merged useAxios Hook: Provides both Axios + API instances.
   ============================================================ */

/**
 * useAxios returns the Axios instance, base URL, auth functions,
 * and (once the authorization header is set) the instantiated API endpoints.
 *
 * Wrap your app (or parts of it) with the `<ProvideAxios>` component.
 */
export function useAPI() {
  const context = useContext(APIContext);
  if (!context) {
    throw new Error('useAxios must be used within a ProvideAPI');
  }

  const { api, baseApiUrl, authHeaderSet, setAuthorization, configuration } =
    context;

  // Use useMemo to instantiate API endpoints once the auth header is set.
  const API: APIType = useMemo(() => {
    return {
      analyticsAPI: new AnalyticsApi(configuration, undefined, api),
      alertsAPI: new AlertsApi(configuration, undefined, api),
      portfoliosAPI: new PortfoliosApi(configuration, undefined, api),
      newsAPI: new NewsApi(configuration, undefined, api),
      feedbackAPI: new FeedbackApi(configuration, undefined, api),
      companiesAPI: new CompaniesApi(configuration, undefined, api),
      dnbAPI: new DnbApi(configuration, undefined, api),
      integrationsApi: new IntegrationsApi(configuration, undefined, api),
      profilesAPI: new ProfilesApi(configuration, undefined, api),
      complianceAPI: new ComplianceApi(configuration, undefined, api),
      tokenAPI: new TokenApi(configuration, undefined, api),
      bindersAPI: new BindersApi(configuration, undefined, api),
      billingAPI: new BillingApi(configuration, undefined, api),
      activitiesAPI: new ActivitiesApi(configuration, undefined, api),
      assistantAPI: new AssistantApi(configuration, undefined, api),
    };
  }, [api, configuration]);

  return {
    api,
    baseApiUrl,
    authHeaderSet,
    setAuthorization,
    configuration,
    API,
  };
}
